import {
  U2000ApiValueToDisplayYmd,
  u2000ShiftCivilYmd,
  u2000TodayOrgYmd,
} from '../../u2000Core/tools/U2000DateTimeHelpers';
import type { U3204ConversationSearchRow } from '../U3204Service';

export type U3204ConversationDayBucket = 'today' | 'yesterday' | 'last7Days' | 'last30Days' | 'older';

export const U3204_CONVERSATION_DAY_BUCKET_ORDER: U3204ConversationDayBucket[] = [
  'today',
  'yesterday',
  'last7Days',
  'last30Days',
  'older',
];

export interface U3204ConversationDayGroup {
  bucket: U3204ConversationDayBucket;
  conversations: U3204ConversationSearchRow[];
}

export interface U3204GroupConversationsByDayOptions {
  timeZone?: string;
}

function u3204CompareCivilYmd(a: string, b: string): number {
  if (a < b) return -1;
  if (a > b) return 1;
  return 0;
}

export function u3204ResolveConversationDayBucket(
  lastModifiedAt: string,
  options?: U3204GroupConversationsByDayOptions,
): U3204ConversationDayBucket | null {
  const ymd = U2000ApiValueToDisplayYmd(lastModifiedAt);
  if (!ymd) return null;

  const tz = options?.timeZone;
  const today = u2000TodayOrgYmd(tz);
  const yesterday = u2000ShiftCivilYmd(today, -1);
  const j7 = u2000ShiftCivilYmd(today, -7);
  const j2 = u2000ShiftCivilYmd(today, -2);
  const j30 = u2000ShiftCivilYmd(today, -30);
  const j8 = u2000ShiftCivilYmd(today, -8);

  if (!yesterday || !j7 || !j2 || !j30 || !j8) return 'older';

  if (ymd === today) return 'today';
  if (ymd === yesterday) return 'yesterday';
  if (u3204CompareCivilYmd(ymd, j7) >= 0 && u3204CompareCivilYmd(ymd, j2) <= 0) return 'last7Days';
  if (u3204CompareCivilYmd(ymd, j30) >= 0 && u3204CompareCivilYmd(ymd, j8) <= 0) return 'last30Days';
  return 'older';
}

export function groupConversationsByDay(
  conversations: U3204ConversationSearchRow[],
  options?: U3204GroupConversationsByDayOptions,
): U3204ConversationDayGroup[] {
  const buckets = new Map<U3204ConversationDayBucket, U3204ConversationSearchRow[]>();

  for (const conv of conversations) {
    const bucket = u3204ResolveConversationDayBucket(conv.lastModifiedAt, options);
    if (!bucket) continue;
    const list = buckets.get(bucket) ?? [];
    list.push(conv);
    buckets.set(bucket, list);
  }

  return U3204_CONVERSATION_DAY_BUCKET_ORDER.filter((bucket) => buckets.has(bucket)).map((bucket) => ({
    bucket,
    conversations: buckets.get(bucket) ?? [],
  }));
}
